import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface Entitlement {
  id: string;
  user_id: string;
  product: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export function useEntitlements() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: entitlements, isLoading, error } = useQuery({
    queryKey: ['entitlements', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const fetchEntitlements = async () => {
        const { data, error } = await supabase
          .from('entitlements')
          .select('*')
          .eq('user_id', user.id);
        if (error) throw error;
        return (data ?? []) as Entitlement[];
      };

      // First attempt: read current entitlement state.
      const initial = await fetchEntitlements();
      if (initial.length > 0) return initial;

      // If user is subscribed in Stripe but the DB row is missing (webhook lag or failure),
      // proactively sync once and re-read — but only if we have a valid session.
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.access_token) {
        try {
          const { data: syncData, error: syncError } = await supabase.functions.invoke('sync-entitlements');
          if (syncError) {
            console.warn('[useEntitlements] sync-entitlements failed:', syncError);
          } else if (syncData?.error) {
            console.warn('[useEntitlements] sync-entitlements returned error:', syncData.error);
          }
        } catch (e) {
          console.warn('[useEntitlements] sync-entitlements exception:', e);
        }
      }

      return await fetchEntitlements();
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });

  // Check if user has an active subscription to a specific product
  const hasActiveSubscription = (product: string = 'industry_insights'): boolean => {
    if (!entitlements) return false;
    
    return entitlements.some(
      (e) => 
        e.product === product && 
        ['active', 'trialing'].includes(e.status) &&
        (!e.current_period_end || new Date(e.current_period_end) > new Date())
    );
  };

  // Get entitlement for a specific product
  const getEntitlement = (product: string = 'industry_insights'): Entitlement | undefined => {
    return entitlements?.find((e) => e.product === product);
  };

  // Determine subscription state for UI routing
  const getSubscriptionState = (product: string = 'industry_insights'): 'none' | 'active' | 'needs_attention' => {
    const entitlement = getEntitlement(product);
    if (!entitlement) return 'none';
    
    // Active or trialing with valid period
    if (['active', 'trialing'].includes(entitlement.status)) {
      if (!entitlement.current_period_end || new Date(entitlement.current_period_end) > new Date()) {
        return 'active';
      }
    }
    
    // Has entitlement but needs attention (past_due, canceled, expired, etc.)
    return 'needs_attention';
  };

  // Create checkout session
  const createCheckout = useMutation({
    mutationFn: async () => {
      // Redirect to subscriber dashboard after successful checkout
      const successUrl = `${window.location.origin}/insights/dashboard?subscription=success`;
      const cancelUrl = `${window.location.origin}/insights?subscription=cancelled`;

      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: { successUrl, cancelUrl },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url; // Redirect in same tab for better flow
      }
    },
    onError: async (error: Error & { alreadySubscribed?: boolean }) => {
      // If server says already subscribed, refresh entitlements and redirect
      if (error.message?.includes('Already subscribed')) {
        console.log('[useEntitlements] Already subscribed, refreshing entitlements');
        await queryClient.invalidateQueries({ queryKey: ['entitlements', user?.id] });
        toast.success('You already have an active subscription!');
        window.location.href = '/insights/dashboard';
        return;
      }
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout');
    },
  });

  // Open customer portal to manage subscription
  const openCustomerPortal = useMutation({
    mutationFn: async () => {
      const returnUrl = window.location.href;

      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { returnUrl },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    },
    onError: (error: Error) => {
      console.error('Portal error:', error);
      toast.error(error.message || 'Failed to open subscription management');
    },
  });

  // Refresh entitlements
  const refreshEntitlements = () => {
    queryClient.invalidateQueries({ queryKey: ['entitlements', user?.id] });
  };

  return {
    entitlements,
    isLoading,
    error,
    hasActiveSubscription,
    getEntitlement,
    getSubscriptionState,
    createCheckout,
    openCustomerPortal,
    refreshEntitlements,
    isSubscribed: hasActiveSubscription('industry_insights'),
    subscriptionState: getSubscriptionState('industry_insights'),
  };
}
